import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import { useFormValidation } from 'Hooks/useFormValidation';
import { useCountdown } from 'Hooks/useCountdown';
import { ROUTES } from 'Utils/routes';
import { ConfirmSignUpFormErrors } from 'Types/forms';
import Copyright from 'Components/Copyright/Copyright';
import FormTextField from 'Components/FormTextField/FormTextField';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

export default function ConfirmSignUp() {
  const { email, username, confirmSignUp, resendConfirmationCode } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    values,
    errors,
    setFieldValue,
    clearFieldError,
    validateEmail,
    validateRequired,
    validateAll,
  } = useFormValidation<ConfirmSignUpFormErrors>({ email, code: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { secondsLeft, isActive: isCooldown, start: startCooldown } = useCountdown(30);

  const validateForm = (): boolean => {
    return validateAll([
      () => validateEmail('email'),
      () => validateRequired('code', 'Verification code is required'),
    ]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    const result = await confirmSignUp(username, values.code);
    setIsLoading(false);

    if (result.success) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    } else {
      showNotification(result.message);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    const result = await resendConfirmationCode(username);
    setIsResending(false);
    if (result.success) {
      startCooldown();
      showNotification('Verification code sent');
    } else {
      showNotification(result.message);
    }
  };

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.dark' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          Confirm sign up
        </Typography>
        <Box component='form' noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormTextField
                required
                name='email'
                label='Email Address'
                value={values.email}
                fieldError={errors.email}
                disabled={isLoading}
                onFieldChange={setFieldValue}
                onClearError={clearFieldError}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                required
                name='code'
                label='Verification Code'
                autoFocus
                value={values.code}
                fieldError={errors.code}
                disabled={isLoading}
                onFieldChange={setFieldValue}
                onClearError={clearFieldError}
              />
            </Grid>
          </Grid>
          <Button
            type='submit'
            color='secondary'
            fullWidth
            variant='contained'
            disabled={isLoading}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Confirm'}
          </Button>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <Button
                variant='text'
                color='info'
                disabled={isResending || isLoading || isCooldown}
                onClick={handleResendCode}
              >
                {isResending ? (
                  <CircularProgress size={20} color='inherit' />
                ) : isCooldown ? (
                  `Resend code (${secondsLeft}s)`
                ) : (
                  'Resend code'
                )}
              </Button>
            </Grid>
            <Grid item>
              <Link href={ROUTES.SIGN_IN} color='secondary.contrastText' variant='body2'>
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}
